import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/shared/lib/cn';

type SwitchSize = 'sm' | 'md';

interface SwitchProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  checked: boolean;
  onChange: (next: boolean) => void;
  size?: SwitchSize;
  /** Optional label rendered to the right of the track. */
  label?: string;
  /** Visually-hidden label for screen readers when no inline label is rendered. */
  ariaLabel?: string;
}

const SIZE_TRACK: Record<SwitchSize, string> = {
  sm: 'h-4 w-7',
  md: 'h-5 w-9',
};

const SIZE_THUMB: Record<SwitchSize, string> = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
};

const SIZE_TRANSLATE: Record<SwitchSize, string> = {
  sm: 'translate-x-3',
  md: 'translate-x-4',
};

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, onChange, size = 'md', label, ariaLabel, disabled, className, ...rest },
  ref,
) {
  const trackOn = 'bg-primary-600';
  const trackOff = 'bg-surface-tertiary';

  const button = (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={!label ? ariaLabel : undefined}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex shrink-0 cursor-pointer items-center rounded-full border border-transparent',
        'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        SIZE_TRACK[size],
        checked ? trackOn : trackOff,
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none inline-block translate-x-0.5 transform rounded-full bg-white shadow ring-0 transition-transform',
          SIZE_THUMB[size],
          checked && SIZE_TRANSLATE[size],
        )}
      />
    </button>
  );

  if (!label) return button;
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 select-none">
      {button}
      <span className="text-sm text-text-primary">{label}</span>
    </label>
  );
});
